import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  EldKeyBody,
  legalMigration,
  MigrationDirection,
  MigrationStatus,
} from '../../../API/TT_ELD/legalMigration';

const STATUS_POLL_MS = 5000;
// Once queued imports are finished a migration can stay "running" for days waiting on pending
// drivers (they switch when they go Off Duty), so poll slowly in that phase
const PENDING_ONLY_POLL_MS = 30000;

// Every queued driver has finished; only pending drivers (if any) are left
export const queuedImportsFinished = (status: MigrationStatus) =>
  status.driversDone + status.driversFailed + status.driversPending >= status.driversTotal;

export const useEldKeys = () =>
  useQuery(['eld-keys'], () => legalMigration.listEldKeys(), { refetchOnWindowFocus: false });

export const useCompanyEldKey = (companyId: string | number | undefined) =>
  useQuery(['eld-keys/company', companyId], () => legalMigration.companyEldKey(companyId!), {
    refetchOnWindowFocus: false,
    enabled: !!companyId,
  });

// Any key change can affect which key a company resolves to
const useEldKeyMutation = <TVars>(fn: (vars: TVars) => Promise<unknown>) => {
  const queryClient = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => {
      queryClient.invalidateQueries('eld-keys');
      queryClient.invalidateQueries('eld-keys/company');
    },
  });
};

export const useCreateEldKey = () =>
  useEldKeyMutation((body: EldKeyBody) => legalMigration.createEldKey(body));
export const useUpdateEldKey = () =>
  useEldKeyMutation(({ id, body }: { id: number; body: EldKeyBody }) =>
    legalMigration.updateEldKey(id, body),
  );
export const useDeleteEldKey = () =>
  useEldKeyMutation((id: number) => legalMigration.deleteEldKey(id));
export const useSetDefaultEldKey = () =>
  useEldKeyMutation((id: number) => legalMigration.setDefaultEldKey(id));

// Polls while the migration is running, stops once it is done / partial / failed
export const useMigrationStatus = (companyId: string | number | undefined) =>
  useQuery(['legal-migration-status', companyId], () => legalMigration.migrationStatus(companyId!), {
    refetchOnWindowFocus: false,
    enabled: !!companyId,
    refetchInterval: (data) => {
      if (data?.status !== 'running') return false;
      return queuedImportsFinished(data) ? PENDING_ONLY_POLL_MS : STATUS_POLL_MS;
    },
  });

// Starting / retrying a migration changes the company, its key and the status
const useMigrationMutation = <TVars>(
  companyId: string | number | undefined,
  fn: (vars: TVars) => Promise<unknown>,
) => {
  const queryClient = useQueryClient();
  return useMutation(fn, {
    onSettled: () => {
      queryClient.invalidateQueries(['legal-migration-status', companyId]);
      queryClient.invalidateQueries(['eld-keys/company', companyId]);
      queryClient.invalidateQueries(`companies/${companyId}`);
    },
  });
};

export const useMigrateToLegal = (companyId: string | number | undefined) =>
  useMigrationMutation(companyId, (eldKeyId?: number | null) =>
    legalMigration.migrateToLegal(companyId!, eldKeyId),
  );
export const useMigrateToNonLegal = (companyId: string | number | undefined) =>
  useMigrationMutation(companyId, (_: void) => legalMigration.migrateToNonLegal(companyId!));
export const useRetryMigration = (companyId: string | number | undefined) =>
  useMigrationMutation(companyId, (direction: MigrationDirection) =>
    legalMigration.retryMigration(companyId!, direction),
  );

export const useChangeSubscriptionPrice = (companyId: string | number | undefined) =>
  useMigrationMutation(companyId, (priceId?: string) =>
    legalMigration.changeSubscriptionPrice(companyId!, priceId),
  );
